import { useCallback } from 'react'
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { useIntl, type IntlShape } from 'react-intl'

import { ApiError, RecordChangedError, type FieldRule } from '@/shared/api/client'
import { useFormattingLocale } from '@/shared/i18n/I18nProvider'

/** The `type` a field error the API sent carries; its message is already the text to show. */
export const serverErrorType = 'server'

/** A value a rule's message fills in: a bound, the item the rule is about, or the other items it names. */
type RuleValue = 'min' | 'max' | 'name' | 'names'

/** The catalog message for each rule code the API sends, and the values it needs. */
const ruleMessages: Readonly<Record<string, { readonly id: string; readonly needs?: readonly RuleValue[] }>> = {
  'field.invalid': { id: 'forms.field.invalid' },
  'field.required': { id: 'forms.rule.required' },
  'field.min_length': { id: 'forms.rule.minLength', needs: ['min'] },
  'field.max_length': { id: 'forms.rule.maxLength', needs: ['max'] },
  'field.max_items': { id: 'forms.rule.maxItems', needs: ['max'] },
  'field.range': { id: 'forms.rule.range', needs: ['min', 'max'] },
  'field.min_value': { id: 'forms.rule.minValue', needs: ['min'] },
  'field.not_a_choice': { id: 'forms.rule.notAChoice' },
  'field.not_found': { id: 'forms.rule.notFound' },
  'field.email': { id: 'forms.rule.email' },
  'field.email_repeated': { id: 'forms.rule.emailRepeated' },
  'field.link': { id: 'forms.rule.link' },
  'field.https_link': { id: 'forms.rule.httpsLink' },
  'field.internal_path': { id: 'forms.rule.internalPath' },
  'field.base_url': { id: 'forms.rule.baseUrl' },
  'field.email_domain': { id: 'forms.rule.emailDomain' },
  'field.time_zone': { id: 'forms.rule.timeZone' },
  'field.culture': { id: 'forms.rule.culture' },
  'field.country': { id: 'forms.rule.country' },
  'field.country_name': { id: 'forms.rule.countryName' },
  'field.organization_not_on_site': { id: 'forms.rule.organizationNotOnSite' },
  'field.own_manager': { id: 'forms.rule.ownManager' },
  'field.email_in_use': { id: 'forms.rule.emailInUse' },
  'field.email_on_site': { id: 'forms.rule.emailOnSite' },
  'field.external_id_in_use': { id: 'forms.rule.externalIdInUse' },
  'field.max_value': { id: 'forms.rule.maxValue', needs: ['max'] },
  'field.min_items': { id: 'forms.rule.minItems', needs: ['min'] },
  'field.amount': { id: 'forms.rule.amount', needs: ['min', 'max'] },
  'field.positive_amount': { id: 'forms.rule.positiveAmount', needs: ['max'] },
  'field.decimal_places': { id: 'forms.rule.decimalPlaces', needs: ['max'] },
  'field.too_long': { id: 'forms.rule.tooLong' },
  'field.repeated': { id: 'forms.rule.repeated' },
  'field.itself': { id: 'forms.rule.itself' },
  'field.not_applicable': { id: 'forms.rule.notApplicable' },
  'field.after_start': { id: 'forms.rule.afterStart' },
  'field.in_future': { id: 'forms.rule.inFuture' },
  'field.not_permitted': { id: 'forms.rule.notPermitted' },
  'field.organization_deactivated': { id: 'forms.rule.organizationDeactivated' },
  'field.person_merged': { id: 'forms.rule.personMerged' },
  'field.host_not_found': { id: 'forms.rule.hostNotFound' },
  'field.address_not_public': { id: 'forms.rule.addressNotPublic' },
  'field.image_type': { id: 'forms.rule.imageType' },
  'field.max_bytes': { id: 'forms.rule.maxBytes', needs: ['max'] },
  'field.control_character': { id: 'forms.rule.controlCharacter' },
  'field.ascii_only': { id: 'forms.rule.asciiOnly' },
  'field.header_name': { id: 'forms.rule.headerName' },
  'field.header_reserved': { id: 'forms.rule.headerReserved' },
  'field.certificate': { id: 'forms.rule.certificate' },
  'field.private_key': { id: 'forms.rule.privateKey' },
  'field.certificate_expired': { id: 'forms.rule.certificateExpired' },
  'field.item_count': { id: 'forms.rule.itemCount', needs: ['min', 'max'] },
  'field.disabled': { id: 'forms.rule.disabled' },
  'field.delivery': { id: 'forms.rule.delivery' },
  'field.https_link_no_query': { id: 'forms.rule.httpsLinkNoQuery' },
  'field.api_version': { id: 'forms.rule.apiVersion', needs: ['max'] },
  'field.parameter_name': { id: 'forms.rule.parameterName', needs: ['max'] },
  'field.parameter_value': { id: 'forms.rule.parameterValue', needs: ['max'] },
  'field.file_type': { id: 'forms.rule.fileType' },
  'field.file_name': { id: 'forms.rule.fileName' },
  'field.file_empty': { id: 'forms.rule.fileEmpty' },
  'field.file_too_large': { id: 'forms.rule.fileTooLarge' },
  'field.upload_missing': { id: 'forms.rule.uploadMissing' },
  'field.zip_invalid': { id: 'forms.rule.zipInvalid' },
  'field.zip_unsafe': { id: 'forms.rule.zipUnsafe' },
  'field.zip_too_large': { id: 'forms.rule.zipTooLarge' },
  'field.activities_outlast_course': { id: 'forms.rule.activitiesOutlastCourse' },
  'field.default_capacity_over_absolute': { id: 'forms.rule.defaultCapacityOverAbsolute' },
  'field.absolute_capacity_needed': { id: 'forms.rule.absoluteCapacityNeeded' },
  'field.one_correct_answer': { id: 'forms.rule.oneCorrectAnswer' },
  'field.correct_answer_needed': { id: 'forms.rule.correctAnswerNeeded' },
  'field.lab_memory': { id: 'forms.rule.labMemory', needs: ['max'] },
  'field.shared_lab_self_paced': { id: 'forms.rule.sharedLabSelfPaced' },
  'field.shared_lab_missing': { id: 'forms.rule.sharedLabMissing' },
  'field.participant_lab_missing': { id: 'forms.rule.participantLabMissing' },
  'field.storage_account_name': { id: 'forms.rule.storageAccountName' },
  'field.storage_account_key': { id: 'forms.rule.storageAccountKey' },
  'field.password_needed': { id: 'forms.rule.passwordNeeded' },
  'field.password_weak': { id: 'forms.rule.passwordWeak', needs: ['min', 'max'] },
  'field.password_refused': { id: 'forms.rule.passwordRefused' },
  'field.package_kind_unknown': { id: 'forms.rule.packageKindUnknown' },
  'field.package_kind_changed': { id: 'forms.rule.packageKindChanged', needs: ['name'] },
  'field.package_path': { id: 'forms.rule.packagePath', needs: ['name'] },
  'field.package_case_clash': { id: 'forms.rule.packageCaseClash', needs: ['name'] },
  'field.package_launch_missing': { id: 'forms.rule.packageLaunchMissing', needs: ['name'] },
  'field.manifest_unreadable': { id: 'forms.rule.manifestUnreadable', needs: ['name'] },
  'field.cmi5_structure': { id: 'forms.rule.cmi5Structure' },
  'field.cmi5_course': { id: 'forms.rule.cmi5Course' },
  'field.cmi5_unit_count': { id: 'forms.rule.cmi5UnitCount', needs: ['max'] },
  'field.cmi5_unit_none': { id: 'forms.rule.cmi5UnitNone' },
  'field.cmi5_unit_id_repeated': { id: 'forms.rule.cmi5UnitIdRepeated', needs: ['name'] },
  'field.cmi5_unit_id_missing': { id: 'forms.rule.cmi5UnitIdMissing', needs: ['name'] },
  'field.cmi5_unit_id_too_long': { id: 'forms.rule.cmi5UnitIdTooLong', needs: ['name', 'max'] },
  'field.cmi5_unit_url_missing': { id: 'forms.rule.cmi5UnitUrlMissing', needs: ['name'] },
  'field.cmi5_unit_url_control': { id: 'forms.rule.cmi5UnitUrlControl', needs: ['name'] },
  'field.cmi5_unit_url_too_long': { id: 'forms.rule.cmi5UnitUrlTooLong', needs: ['name', 'max'] },
  'field.cmi5_unit_https': { id: 'forms.rule.cmi5UnitHttps', needs: ['name'] },
  'field.cmi5_unit_launch': { id: 'forms.rule.cmi5UnitLaunch', needs: ['name'] },
  'field.cmi5_unit_launch_method': { id: 'forms.rule.cmi5UnitLaunchMethod', needs: ['name'] },
  'field.cmi5_unit_move_on': { id: 'forms.rule.cmi5UnitMoveOn', needs: ['name'] },
  'field.cmi5_unit_mastery_score': { id: 'forms.rule.cmi5UnitMasteryScore', needs: ['name'] },
  'field.scorm_version': { id: 'forms.rule.scormVersion', needs: ['name'] },
  'field.scorm_items_too_deep': { id: 'forms.rule.scormItemsTooDeep', needs: ['max'] },
  'field.scorm_resource_path': { id: 'forms.rule.scormResourcePath', needs: ['max'] },
  'field.scorm_nothing_to_launch': { id: 'forms.rule.scormNothingToLaunch' },
  'field.scorm_item_resource': { id: 'forms.rule.scormItemResource', needs: ['name'] },
  'field.tag_needs': { id: 'forms.rule.tagNeeds', needs: ['name', 'names'] },
  'field.tag_needs_unusable': { id: 'forms.rule.tagNeedsUnusable', needs: ['name'] },
  'field.tag_group_exclusive': { id: 'forms.rule.tagGroupExclusive', needs: ['name'] },
  'field.publishing_group_full': { id: 'forms.rule.publishingGroupFull', needs: ['name', 'max'] },
}

/** Whether the rule carries a value its message needs; a list of names needs at least one. */
function carries(rule: FieldRule, value: RuleValue): boolean {
  return value === 'names' ? (rule.names ?? []).length > 0 : rule[value] !== null
}

/**
 * The text for a rule, in the person's language with its bounds in their culture's number format and
 * the items it names as the API sent them; `null` for a code the UI does not know, or one missing a
 * value it needs.
 */
function ruleText(intl: IntlShape, numbers: Intl.NumberFormat, rule: FieldRule | null): string | null {
  const known = rule !== null && Object.hasOwn(ruleMessages, rule.code) ? ruleMessages[rule.code] : undefined
  if (rule === null || known === undefined || (known.needs ?? []).some((value) => !carries(rule, value))) {
    return null
  }
  return intl.formatMessage(
    { id: known.id },
    {
      min: numbers.format(rule.min ?? 0),
      max: numbers.format(rule.max ?? 0),
      name: rule.name ?? '',
      names: intl.formatList(rule.names ?? [], { type: 'disjunction' }),
    },
  )
}

/**
 * The text to show for each of a refusal's field entries: its rule's, from the catalog, so it is
 * in the person's language; else the API's own entry, for a rule with no code yet. The same text
 * twice on one field shows once.
 */
export function fieldErrorMessages(intl: IntlShape, error: ApiError, locale: string = intl.locale): Readonly<Record<string, readonly string[]>> {
  const numbers = new Intl.NumberFormat(locale)
  // fromEntries, so a field named `__proto__` stays an ordinary key.
  return Object.fromEntries(
    Object.entries(error.fieldErrors).map(([field, messages]) => {
      const rules = Object.hasOwn(error.fieldRules, field) ? (error.fieldRules[field] ?? []) : []
      return [field, [...new Set(messages.map((message, index) => ruleText(intl, numbers, rules[index] ?? null) ?? message))]]
    }),
  )
}

/** `fieldErrorMessages` for the person's language and culture. */
export type FieldErrorText = (error: ApiError) => Readonly<Record<string, readonly string[]>>

/** `fieldErrorMessages` in the surrounding language, with numbers in the person's culture even when its messages fall back. */
export function useFieldErrorText(): FieldErrorText {
  const intl = useIntl()
  const locale = useFormattingLocale()
  return useCallback((error: ApiError) => fieldErrorMessages(intl, error, locale), [intl, locale])
}

/**
 * Puts each field error the API sent on its field, by its camelCase name (`address.city`), when
 * the form shows that field, so it is shown and announced there. Returns the messages for
 * fields it does not show. Both are in the person's language where the API names the rule.
 */
export function applyServerErrors<V extends FieldValues, O = V>(
  form: Pick<UseFormReturn<V, unknown, O>, 'control' | 'setError'>,
  error: unknown,
  text: FieldErrorText,
): readonly string[] {
  if (!(error instanceof ApiError)) {
    return []
  }
  // The fields registered and rendered now: a value in the defaults alone has nowhere to show.
  // React Hook Form has no public list of them.
  // oxlint-disable-next-line eslint/no-underscore-dangle
  const shown = form.control._names.mount
  const unplaced: string[] = []
  for (const [field, messages] of Object.entries(text(error))) {
    const message = messages.join(' ')
    if (shown.has(field)) {
      form.setError(field as Path<V>, { type: serverErrorType, message })
    } else {
      unplaced.push(message)
    }
  }
  return unplaced
}

/** Where a form shows refusals the API does not name by one of the form's own fields. */
export interface RefusalPlaces {
  /** The form's field, or fields, for each field the API names otherwise, such as `organizationId`. */
  readonly fields?: Readonly<Record<string, string | readonly string[]>>
  /** The form's field and the message for each refusal the API names by its code alone. */
  readonly codes?: Readonly<Record<string, { readonly field: string; readonly message: string }>>
}

/**
 * The API's refusal with its field errors, and their rules, under the form's own field names, and
 * a refusal named by its code alone put on the field it is about, so `applyServerErrors` shows
 * each on its field rather than only in the form's alert. Anything else is returned as it is.
 */
export function placeRefusal(error: unknown, places: RefusalPlaces): unknown {
  if (!(error instanceof ApiError) || error instanceof RecordChangedError) {
    return error
  }
  // Own keys only, and no prototype, so a field the API names `__proto__` stays an ordinary key.
  const fieldErrors = Object.create(null) as Record<string, string[]>
  const fieldRules = Object.create(null) as Record<string, (FieldRule | null)[]>
  const add = (field: string, messages: readonly string[], rules: readonly (FieldRule | null)[]) => {
    fieldErrors[field] = [...(Object.hasOwn(fieldErrors, field) ? (fieldErrors[field] ?? []) : []), ...messages]
    fieldRules[field] = [...(Object.hasOwn(fieldRules, field) ? (fieldRules[field] ?? []) : []), ...rules]
  }
  const fields = places.fields ?? {}
  for (const [field, messages] of Object.entries(error.fieldErrors)) {
    const sent = Object.hasOwn(error.fieldRules, field) ? (error.fieldRules[field] ?? []) : []
    const rules = messages.map((_, index) => sent[index] ?? null)
    const targets = Object.hasOwn(fields, field) ? (fields[field] ?? field) : field
    for (const target of typeof targets === 'string' ? [targets] : targets) {
      add(target, messages, rules)
    }
  }
  const codes = places.codes ?? {}
  const byCode = error.code !== null && Object.hasOwn(codes, error.code) ? codes[error.code] : undefined
  if (byCode !== undefined) {
    add(byCode.field, [byCode.message], [null])
  }
  return new ApiError(error.message, error.status, {
    cause: error,
    problem: {
      title: error.title,
      detail: error.detail,
      code: error.code,
      correlationId: error.correlationId,
      fieldErrors,
      fieldRules,
    },
  })
}

/**
 * Marks the values a save sent (`sent`, read as it began) as the unchanged ones. The current
 * values stay, so anything that changed them since still counts as unsaved.
 */
export function markSaved<V extends FieldValues, O = V>(
  form: Pick<UseFormReturn<V, unknown, O>, 'reset'>,
  sent: V,
): void {
  form.reset(sent, { keepValues: true })
}
